// ---------------------------------------------------------------------------
// Reveal-on-scroll.
//
// Marks up nothing and hides nothing on its own: a container opts in with
// `data-reveal` and its items with `data-reveal-item`, and the hidden starting
// state in main.css only applies once this file has stamped `data-reveal-ready`
// on the container. So if the bundle fails, or IntersectionObserver is missing,
// or the visitor has asked for reduced motion, the content is simply there —
// there is no state in which JS is required to see it.
//
// Items always reveal in DOM order, which is not the order they cross the line
// in. The cards block staggers its columns vertically, so the raised cards trip
// the observer together and the dropped one arrives later — animating on
// arrival gives 1, 3, 2. Instead an item that has crossed waits in `pending`
// and the queue only drains from the front: card 3 holds until card 2 has come
// in, and then the two go one after another.
//
// Everything that drains in the same pass is stepped by STAGGER, so a run reads
// as one cascade, while an item reached on its own animates immediately rather
// than sitting out the delays of the items above it.
// ---------------------------------------------------------------------------

const STAGGER = 180;

// Fires a little before the item is fully on screen, so the movement reads as
// part of the scroll rather than as something that happens after it.
const OBSERVER_OPTIONS = {
  rootMargin: '0px 0px -10% 0px',
  threshold: 0.15,
};

function revealContainer(container) {
  // Direct children only, matching the CSS — a nested [data-reveal] container
  // runs its own queue.
  const items = [...container.querySelectorAll(':scope > [data-reveal-item]')];
  if (!items.length) return;

  container.setAttribute('data-reveal-ready', '');

  const pending = new Set();
  let cursor = 0;

  const drain = () => {
    let step = 0;

    while (cursor < items.length) {
      const item = items[cursor];

      if (pending.has(item)) {
        item.style.transitionDelay = `${step * STAGGER}ms`;
        item.setAttribute('data-revealed', '');
        pending.delete(item);

        cursor++;
        step++;
        continue;
      }

      // An item with no box at all is one a breakpoint has hidden. It can never
      // intersect, so waiting on it would hold every later item invisible —
      // step over it instead. Anything merely below the fold still has rects.
      if (!item.getClientRects().length) {
        cursor++;
        continue;
      }

      break;
    }
  };

  const observer = new IntersectionObserver((entries, self) => {
    entries
      .filter(entry => entry.isIntersecting)
      .forEach(entry => {
        pending.add(entry.target);
        // One-shot: nothing re-hides on the way back up.
        self.unobserve(entry.target);
      });

    drain();
  }, OBSERVER_OPTIONS);

  items.forEach(item => observer.observe(item));
}

export default function initReveal() {
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  document.querySelectorAll('[data-reveal]').forEach(revealContainer);
}
